import type { FC, ReactNode } from 'react'

import { useId } from 'react'

interface MoonPhaseProps {
    /** 0 is a new moon, 0.5 is full, 1 is new again. */
    phase: number
    size?: number
    className?: string
    color?: string
    shadow?: string
    glow?: boolean
}

/**
 * Draws a moon at any phase as a lit disc with a curved terminator.
 * Purely decorative, so it is hidden from screen readers.
 */
export const MoonPhase: FC<MoonPhaseProps> = ({ phase, size = 24, className, color = '#E9E4DA', shadow = 'rgba(233, 228, 218, 0.08)', glow = false }): ReactNode => {
    const id = useId().replace(/:/g, '')
    const p = ((phase % 1) + 1) % 1
    const r = 10
    const cx = 12
    const cy = 12

    const illumination = p <= 0.5 ? p * 2 : (1 - p) * 2
    const waxing = p <= 0.5
    const terminator = Math.abs(1 - illumination * 2) * r
    const litOnRight = waxing

    const outerSweep = litOnRight ? 1 : 0
    const innerSweep = illumination > 0.5 ? (litOnRight ? 1 : 0) : (litOnRight ? 0 : 1)

    const path = illumination <= 0.01
        ? ''
        : illumination >= 0.99
            ? `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx} ${cy + r} A ${r} ${r} 0 1 1 ${cx} ${cy - r} Z`
            : `M ${cx} ${cy - r} A ${r} ${r} 0 0 ${outerSweep} ${cx} ${cy + r} A ${terminator} ${r} 0 0 ${innerSweep} ${cx} ${cy - r} Z`

    return (
        <svg width={size} height={size} viewBox='0 0 24 24' className={className} aria-hidden='true' focusable='false'>
            {glow && (
                <defs>
                    <radialGradient id={`glow-${id}`}>
                        <stop offset='55%' stopColor={color} stopOpacity='0.35' />
                        <stop offset='100%' stopColor={color} stopOpacity='0' />
                    </radialGradient>
                </defs>
            )}

            {glow && <circle cx={cx} cy={cy} r={12} fill={`url(#glow-${id})`} />}

            <circle cx={cx} cy={cy} r={r} fill={shadow} stroke={color} strokeOpacity='0.28' strokeWidth='0.5' />

            {path && <path d={path} fill={color} />}
        </svg>
    )
}

/** The eight phases used to mark the eight emotions and section dividers. */
export const phaseForIndex = (index: number, total = 8): number => ((index % total) + 0.5) / total
